import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Alert,
  Chip,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useNotification } from "@context/NotificationContext";
import { useLocalKeys } from "@hooks/useLocalKeys";
import * as imagesApi from "@api/images.api";
import { extractErrorMessage } from "@api/axios";
import { UPLOAD_ITEM_STATUS, type UploadItemStatus } from "@/constants/upload";

interface UploadItem {
  file: File;
  progress: number;
  status: UploadItemStatus;
  error?: string;
}

export default function Upload() {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { storeKey } = useLocalKeys();
  const [files, setFiles] = useState<UploadItem[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const items: UploadItem[] = Array.from(newFiles).map((file) => ({
      file,
      progress: 0,
      status: UPLOAD_ITEM_STATUS.pending,
    }));
    setFiles((prev) => [...prev, ...items]);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles],
  );

  const handleFileSelect = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "image/*";
    input.onchange = () => {
      if (input.files?.length) {
        addFiles(input.files);
      }
    };
    input.click();
  }, [addFiles]);

  const uploadAll = useCallback(async () => {
    for (let i = 0; i < files.length; i++) {
      if (files[i].status !== UPLOAD_ITEM_STATUS.pending) continue;

      setFiles((prev) =>
        prev.map((f, idx) =>
          idx === i
            ? { ...f, status: UPLOAD_ITEM_STATUS.uploading, progress: 30 }
            : f,
        ),
      );

      try {
        const result = await imagesApi.upload(files[i].file);

        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i
              ? { ...f, status: UPLOAD_ITEM_STATUS.encrypting, progress: 70 }
              : f,
          ),
        );

        await storeKey(result.image.id, result.encryptedUnlockKey);

        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i
              ? { ...f, status: UPLOAD_ITEM_STATUS.done, progress: 100 }
              : f,
          ),
        );

        notify(t("upload.keySaved", { name: result.image.name }), "success");
      } catch (err) {
        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i
              ? {
                  ...f,
                  status: UPLOAD_ITEM_STATUS.error,
                  error: extractErrorMessage(err),
                }
              : f,
          ),
        );
      }
    }
  }, [files, storeKey, notify, t]);

  const pendingCount = files.filter(
    (f) => f.status === UPLOAD_ITEM_STATUS.pending,
  ).length;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 600,
        }}
      >
        {t("upload.title")}
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        {t("upload.localStorageWarning")}
      </Alert>
      {/* Drop zone */}
      <Card
        sx={{
          mb: 3,
          border: "2px dashed",
          borderColor: dragOver ? "primary.main" : "divider",
          bgcolor: dragOver ? "action.hover" : "background.paper",
          cursor: "pointer",
          transition: "all 0.2s",
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={handleFileSelect}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 6,
          }}
        >
          <CloudUploadIcon
            sx={{ fontSize: 56, color: "primary.main", mb: 2 }}
          />
          <Typography variant="h6">{t("upload.dropHere")}</Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {t("upload.orClick")}
          </Typography>
        </CardContent>
      </Card>
      {/* File list */}
      {files.length > 0 && (
        <>
          <List>
            {files.map((item, i) => (
              <ListItem
                key={i}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <ListItemIcon>
                  {item.status === UPLOAD_ITEM_STATUS.done ? (
                    <CheckIcon color="success" />
                  ) : item.status === UPLOAD_ITEM_STATUS.error ? (
                    <ErrorIcon color="error" />
                  ) : (
                    <FileIcon />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.file.name}
                  secondary={
                    item.status === UPLOAD_ITEM_STATUS.error
                      ? item.error
                      : item.status === UPLOAD_ITEM_STATUS.encrypting
                        ? t("upload.encryptingDetail")
                        : item.status === UPLOAD_ITEM_STATUS.done
                          ? t("upload.complete")
                          : `${(item.file.size / 1024).toFixed(1)} KB`
                  }
                />
                <Box
                  sx={{
                    minWidth: 120,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {item.status === UPLOAD_ITEM_STATUS.uploading ||
                  item.status === UPLOAD_ITEM_STATUS.encrypting ? (
                    <LinearProgress
                      variant="determinate"
                      value={item.progress}
                      sx={{ width: "100%" }}
                    />
                  ) : (
                    <Chip
                      label={t(`upload.status.${item.status}`)}
                      size="small"
                      color={
                        item.status === UPLOAD_ITEM_STATUS.done
                          ? "success"
                          : item.status === UPLOAD_ITEM_STATUS.error
                            ? "error"
                            : "default"
                      }
                    />
                  )}
                </Box>
              </ListItem>
            ))}
          </List>

          {pendingCount > 0 && (
            <Button
              variant="contained"
              size="large"
              startIcon={<CloudUploadIcon />}
              onClick={uploadAll}
              sx={{ mt: 2 }}
              fullWidth
            >
              {t("upload.uploadAll", { count: pendingCount })}
            </Button>
          )}
        </>
      )}
    </Box>
  );
}
